import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Shield, Users, Heart, CheckCircle, Star, Mail, Lock, Eye, EyeOff, MapPin, Calendar, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { premiumAPI, authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const HomePage = () => {
  const [premiumPlans, setPremiumPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const { isAuthenticated, user, loadUser, login } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { toast } = useToast();

  // State for premium plans
  const [loading, setLoading] = useState(false);
  
  // State for homepage content
  const [successStories, setSuccessStories] = useState([]);
  const [homepageBanners, setHomepageBanners] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [homepageSections, setHomepageSections] = useState([]);

  const handleComingSoon = (feature) => {
    toast({
      title: "Coming Soon",
      description: `${feature} feature will be available soon!`,
    });
  };

  // Check if a section is enabled
  const isSectionEnabled = (sectionId) => {
    if (homepageSections.length === 0) return true; // Default to showing all
    const section = homepageSections.find(s => s.id === sectionId);
    return section ? section.enabled : true;
  };

  // Get sections in order
  const getSortedSections = () => {
    if (homepageSections.length === 0) {
      return ['hero', 'registration', 'why_choose_us', 'success_stories', 'cta'];
    }
    return homepageSections
      .filter(s => s.enabled)
      .sort((a, b) => a.order - b.order)
      .map(s => s.id);
  };

  // Load functions - declared before useEffect
  const loadPremiumPlans = async () => {
    try {
      const data = await premiumAPI.getPlans();
      setPremiumPlans(data.plans || []);
    } catch (error) {
      console.error('Failed to load premium plans:', error);
    }
  };

  const loadSuccessStories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/extended/success-stories/public`);
      if (response.ok) {
        const data = await response.json();
        setSuccessStories(data.stories || []);
      }
    } catch (error) {
      console.error('Failed to load success stories:', error);
    }
  };

  const loadHomepageSections = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/homepage-sections/public`);
      if (response.ok) {
        const data = await response.json();
        setHomepageSections(data.sections || []);
      }
    } catch (error) {
      console.error('Failed to load homepage sections:', error);
    }
  };

  const loadHomepageBanners = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/extended/homepage-banners/public`);
      if (response.ok) {
        const data = await response.json();
        setHomepageBanners(data.banners || []);
      }
    } catch (error) {
      console.error('Failed to load homepage banners:', error);
    }
  };

  const loadPremiumStatus = async () => {
    try {
      const data = await premiumAPI.getStatus();
      setPremiumStatus(data);
    } catch (error) {
      console.error('Failed to load premium status:', error);
    }
  };

  useEffect(() => {
    loadPremiumPlans();
    loadSuccessStories();
    loadHomepageBanners();
    loadHomepageSections();
    if (isAuthenticated) {
      loadPremiumStatus();
      // Check if there's a stored plan selection from before login
      const storedPlan = sessionStorage.getItem('selectedPlan');
      if (storedPlan) {
        try {
          const plan = JSON.parse(storedPlan);
          sessionStorage.removeItem('selectedPlan');
          
          // Wait a bit for premiumStatus to load
          setTimeout(async () => {
            await loadPremiumStatus(); // Ensure we have latest status
            
            // Check if user already has this plan
            const status = await premiumAPI.getStatus();
            if (status.isPremium && status.planName === plan.name) {
              toast({
                title: "Already Subscribed",
                description: `You already have the ${plan.name} plan`,
                variant: "destructive",
              });
            } else if (status.isPremium) {
              // User wants to upgrade
              toast({
                title: "Upgrade Available",
                description: `You can upgrade from ${status.planName} to ${plan.name}`,
              });
            } else {
              // New subscriber
              toast({
                title: "Selected Plan",
                description: `${plan.name} plan selected. Click 'Continue to Payment' to proceed.`,
              });
            }
            
            // Always scroll to premium plans section to show them the plans
            setTimeout(() => {
              const element = document.getElementById('premium-plans');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 500);
            
            // Set the selected plan so it shows with RED border
            setSelectedPlan(plan);
          }, 1500);
        } catch (e) {
          console.error('Error parsing stored plan:', e);
        }
      }
    }
  }, [isAuthenticated]);

  // Auto-rotate banners
  useEffect(() => {
    if (homepageBanners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBannerIndex((prev) => (prev + 1) % homepageBanners.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [homepageBanners.length]);

  const handleChoosePlan = async (plan) => {
    // Check if user already has this exact plan
    if (premiumStatus?.isPremium && premiumStatus?.planName === plan.name) {
      // Already have this plan, can't buy again
      toast({
        title: "Already Subscribed",
        description: `You already have the ${plan.name} plan`,
        variant: "destructive",
      });
      return;
    }

    // If this plan is already selected, proceed to next step
    if (selectedPlan?.id === plan.id) {
      // If not logged in, redirect to login
      if (!isAuthenticated) {
        toast({
          title: "Login Required",
          description: "Please login to subscribe to a premium plan",
          variant: "destructive",
        });
        sessionStorage.setItem('selectedPlan', JSON.stringify(plan));
        navigate('/login', { state: { returnUrl: '/' } });
        return;
      }
      // If logged in, go to payment
      navigate('/payment', { state: { plan } });
      return;
    }

    // Otherwise, just select the plan (show RED highlight) - works for everyone
    setSelectedPlan(plan);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Banner Section */}
      {isSectionEnabled('hero') && (
      <section className="relative h-[500px] overflow-hidden">
        {/* Show admin banners if available, otherwise default video */}
        {homepageBanners.length > 0 ? (
          <>
            {/* Banner Image */}
            <div className="absolute inset-0">
              <img
                src={homepageBanners[currentBannerIndex]?.image_url}
                alt={homepageBanners[currentBannerIndex]?.title}
                className="w-full h-full object-cover transition-opacity duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
            </div>
            
            {/* Banner Content */}
            <div className="relative z-10 w-full px-4 md:px-8 h-full flex items-center">
              <div className="max-w-2xl">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white">
                  {homepageBanners[currentBannerIndex]?.title}
                </h1>
                {homepageBanners[currentBannerIndex]?.subtitle && (
                  <p className="text-lg md:text-xl text-gray-200 mb-6">
                    {homepageBanners[currentBannerIndex]?.subtitle}
                  </p>
                )}
                {homepageBanners[currentBannerIndex]?.button_text && homepageBanners[currentBannerIndex]?.link_url && (
                  <Link to={homepageBanners[currentBannerIndex].link_url}>
                    <Button className="bg-mint-500 hover:bg-mint-600 text-white px-8 py-3 text-lg">
                      {homepageBanners[currentBannerIndex].button_text}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
            
            {/* Banner Navigation Dots */}
            {homepageBanners.length > 1 && (
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
                {homepageBanners.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBannerIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentBannerIndex 
                        ? 'bg-white w-8' 
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                  />
                ))}
              </div>
            )}
            
            {/* Banner Navigation Arrows */}
            {homepageBanners.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentBannerIndex((prev) => (prev - 1 + homepageBanners.length) % homepageBanners.length)}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 w-10 h-10 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={() => setCurrentBannerIndex((prev) => (prev + 1) % homepageBanners.length)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 w-10 h-10 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </>
        ) : (
          <>
            {/* Default Video Banner */}
            <div className="absolute inset-0">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              >
                <source src="https://customer-assets.emergentagent.com/job_verifyhub-18/artifacts/1v6xz7d3_20251210_0912_01kc4kvbkffwp9h7rx61n8edh9.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
            </div>
            <div className="relative z-10 w-full px-4 md:px-8 h-full flex items-center">
              <div className="max-w-2xl">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white">
                  Find Your Perfect Muslim Life Partner
                </h1>
                <p className="text-lg md:text-xl text-gray-200">
                  A trusted Islamic matrimonial service connecting Muslims worldwide.
                </p>
              </div>
            </div>
          </>
        )}
      </section>
      )}

      {/* Registration Form - Only for non-logged-in users */}
      {!isAuthenticated && isSectionEnabled('registration') && (
        <section 
          id="registration-form" 
          className="islamic-pattern-bg"
          style={{ background: theme?.registrationBg || 'linear-gradient(135deg, #FDF8F4 0%, #f0fdf4 100%)' }}
        >
            <Card className="shadow-none rounded-none border-0 bg-transparent">
              <CardHeader className="text-center pt-12 pb-8 px-4">
                <CardTitle 
                  className="text-2xl"
                  style={{ color: theme?.registrationText || '#1f2937' }}
                >
                  Join Qbool Today
                </CardTitle>
                <p style={{ color: theme?.registrationText ? `${theme.registrationText}99` : '#6b7280' }}>
                  Find your perfect match with verified profiles
                </p>
              </CardHeader>
              <CardContent className="px-4 md:px-8 lg:px-16 pb-12">
                <div className="space-y-6">
                  {/* Registration Steps */}
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div 
                        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm"
                        style={{ background: theme?.primaryColor || '#E91E63', color: '#ffffff' }}
                      >1</div>
                      <div>
                        <p className="font-semibold" style={{ color: theme?.registrationText || '#1f2937' }}>Create Account</p>
                        <p className="text-sm" style={{ color: theme?.registrationText ? `${theme.registrationText}99` : '#6b7280' }}>Quick registration with email & password</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div 
                        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm"
                        style={{ background: theme?.primaryColor || '#E91E63', color: '#ffffff' }}
                      >2</div>
                      <div>
                        <p className="font-semibold" style={{ color: theme?.registrationText || '#1f2937' }}>Verify Email</p>
                        <p className="text-sm" style={{ color: theme?.registrationText ? `${theme.registrationText}99` : '#6b7280' }}>Confirm your email with verification code</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div 
                        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm"
                        style={{ background: theme?.primaryColor || '#E91E63', color: '#ffffff' }}
                      >3</div>
                      <div>
                        <p className="font-semibold" style={{ color: theme?.registrationText || '#1f2937' }}>Admin Approval</p>
                        <p className="text-sm" style={{ color: theme?.registrationText ? `${theme.registrationText}99` : '#6b7280' }}>Get verified within 24-48 hours</p>
                      </div>
                    </div>
                  </div>

                  {/* Registration Button */}
                  <Link to="/register" className="block">
                    <Button 
                      className="w-full py-6"
                      style={{ background: theme?.buttonPrimary || '#E91E63', color: theme?.buttonPrimaryText || '#ffffff' }}
                    >
                      Get Started - It&apos;s Free!
                    </Button>
                  </Link>

                  <div className="text-center">
                    <p style={{ color: theme?.registrationText ? `${theme.registrationText}99` : '#6b7280' }}>
                      Already have an account?{' '}
                      <Link to="/login" className="font-semibold" style={{ color: theme?.linkColor || '#E91E63' }}>
                        Login
                      </Link>
                    </p>
                  </div>

                  <p className="text-center text-xs" style={{ color: theme?.registrationText ? `${theme.registrationText}80` : '#9ca3af' }}>
                    By registering, you agree to our{' '}
                    <Link to="/terms" style={{ color: theme?.linkColor || '#E91E63' }}>
                      Terms & Conditions
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" style={{ color: theme?.linkColor || '#E91E63' }}>
                      Privacy Policy
                    </Link>
                  </p>
                </div>
              </CardContent>
            </Card>
        </section>
      )}

      {/* Premium Plans - Only show to authenticated users */}
      {/* Moved to /pricing page */}

      {/* Why Choose Us */}
      {isSectionEnabled('why_choose_us') && (
      <section 
        className="py-16 relative overflow-hidden"
        style={{ 
          background: theme?.whyChooseUsBg || `linear-gradient(135deg, ${theme?.secondaryColor || '#1e3a5f'} 0%, ${theme?.primaryColor || '#E91E63'} 100%)`,
          color: theme?.whyChooseUsText || '#ffffff'
        }}
      >
        <div className="absolute inset-0 islamic-pattern-bg opacity-10"></div>
        <div className="w-full px-2 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose qbool?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">100% Halal & Safe</h3>
              <p className="opacity-90">
                All profiles manually verified. Islamic guidelines maintained throughout
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Muslim Matrimonial & Matchmaking</h3>
              <p className="opacity-90">
                Connect with Muslims across the world
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Privacy & Respect</h3>
              <p className="opacity-90">
                Your information protected with Islamic values at the core
              </p>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Success Stories Section */}
      {isSectionEnabled('success_stories') && successStories.length > 0 && (
        <section 
          className="py-16" 
          style={{ backgroundColor: theme?.successStoriesBg || '#FDF8F4' }}
        >
          <div className="w-full px-4 md:px-8">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ color: theme?.successStoriesText || '#232323' }}
              >
                Success Stories
              </h2>
              <p style={{ color: theme?.successStoriesText ? `${theme.successStoriesText}99` : '#6b7280' }} className="max-w-2xl mx-auto">
                Real couples who found their perfect match through Qbool
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {successStories.slice(0, 6).map((story) => (
                <Card key={story.id} className="overflow-hidden hover:shadow-lg transition-shadow bg-white">
                  {story.image_url && (
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={story.image_url} 
                        alt={story.couple_names} 
                        className="w-full h-full object-cover"
                      />
                      {story.is_featured && (
                        <div className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                          <Star className="w-3 h-3 fill-current" /> Featured
                        </div>
                      )}
                    </div>
                  )}
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Heart className="w-5 h-5 text-coral-500 fill-coral-500" />
                      <h3 className="font-semibold text-lg text-gray-900">{story.couple_names}</h3>
                    </div>
                    {(story.location || story.wedding_date) && (
                      <div className="flex flex-wrap gap-3 mb-3 text-sm text-gray-500">
                        {story.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" /> {story.location}
                          </span>
                        )}
                        {story.wedding_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" /> {new Date(story.wedding_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="relative">
                      <Quote className="w-6 h-6 text-mint-300 absolute -top-1 -left-1 opacity-50" />
                      <p className="text-gray-600 text-sm line-clamp-4 pl-4 italic">
                        {story.story}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {isSectionEnabled('cta') && (
      <section 
        className="py-16"
        style={{ 
          background: theme?.ctaBg || theme?.secondaryColor || '#1f2937',
          color: theme?.ctaText || '#ffffff'
        }}
      >
        <div className="w-full px-2 text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold mb-8"
            style={{ color: theme?.ctaText || '#ffffff' }}
          >
            Ready to Complete Half Your Deen?
          </h2>
          {!isAuthenticated ? (
            <Link to="/register">
              <Button 
                className="px-12 py-6 text-lg font-semibold"
                style={{ 
                  background: theme?.buttonPrimary || '#E91E63', 
                  color: theme?.buttonPrimaryText || '#ffffff'
                }}
              >
                Get Started - It&apos;s Free!
              </Button>
            </Link>
          ) : (
            <Link to="/search">
              <Button 
                className="px-12 py-6 text-lg font-semibold"
                style={{ 
                  background: theme?.buttonPrimary || '#E91E63', 
                  color: theme?.buttonPrimaryText || '#ffffff'
                }}
              >
                <Search className="w-6 h-6 mr-2" />
                Start Searching Now
              </Button>
            </Link>
          )}
        </div>
      </section>
      )}
    </div>
  );
};

export default HomePage;